/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : toolKit.jsx
 * description   : {{insert here}}
 * creation date : 17/02/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    Route,
} from 'react-router-dom';

import {
    Fragment,
} from 'react';

import {
    OsTypes,
    BrowserTypes,
} from 'react-device-detect';

import {
    SES,
} from '@aws-sdk/client-ses';

import fuses from '@/styles/app/fuses.scss';



// --------------------------------------------------------------------------------
//      CONSTANTS DEFINITION
// --------------------------------------------------------------------------------

const toolkit_enums_runtime_configTypes =
{
    DEVELOPMENT: 'DEVELOPMENT',
    PRODUCTION: 'PRODUCTION',
};

const toolkit_enums_auth_stateTypes =
{
    UNMOUNTED: 'UNMOUNTED',
    AUTHENTICATED: 'AUTHENTICATED',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
};

const toolkit_enums_auth_procStatusTypes =
{
    SUCCESS: 'SUCCESS',
    WARN_ALREADY_AUTHENTICATED: 'WARN_ALREADY_AUTHENTICATED',
    WARN_ALREADY_UNAUTHENTICATED: 'WARN_ALREADY_UNAUTHENTICATED',
    ERROR_BAD_CREDENTIALS: 'ERROR_BAD_CREDENTIALS',
    ERROR_SIGN_OUT: 'ERROR_SIGN_OUT',
    UNKNOWN: 'UNKNOWN',

    get list() {
        return ([
            this.SUCCESS,
            this.WARN_ALREADY_AUTHENTICATED,
            this.WARN_ALREADY_UNAUTHENTICATED,
            this.ERROR_BAD_CREDENTIALS,
            this.ERROR_SIGN_OUT,
            this.UNKNOWN,
        ]);
    }
};

const toolkit_enums_window_viewPortTypes =
{
    FULL: 'view-port-full',
    LARGE: 'view-port-large',
    MEDIUM: 'view-port-medium',
    SMALL: 'view-port-small',
    XS: 'view-port-xs',
    XXS: 'view-port-xxs',
};

const toolkit_enums_device_deviceTypes =
{
    BROWSER: 'browser',
    MOBILE: 'mobile',
    TABLET: 'tablet',
    SMARTTV: 'smarttv',
    WEARABLE: 'wearable',
    CONSOLE: 'console',
    EMBEDDED: 'embedded',
};

const toolkit_enums_email_statusTypes =
{
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
};



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function toolkit_api_window_getScreenProperties() {
    const result =
    {
        resolution:
        {
            height: window.screen.height,
            width: window.screen.width,
        },
        outer:
        {
            height: window.outerHeight,
            width: window.outerWidth,
        },
        inner:
        {
            height: window.innerHeight,
            width: window.innerWidth,
        },
        ratio:
        {
            height: (window.innerHeight / window.screen.height),
            width: (window.innerWidth / window.screen.width),
        },
        viewPort: '',
    }

    // Compute the view port size
    if (window.innerWidth <= toolkit_api_utils_strToPix(fuses['view-port-medium'])) {
        result.viewPort = toolkit_enums_window_viewPortTypes.SMALL;
    }
    else if (window.innerWidth <= toolkit_api_utils_strToPix(fuses['view-port-large'])) {
        result.viewPort = toolkit_enums_window_viewPortTypes.MEDIUM;
    }
    else {
        result.viewPort = toolkit_enums_window_viewPortTypes.LARGE;
    }

    return result;
}

function toolkit_api_window_scrollTo(target, reference = undefined, behavior = 'smooth') {
    var selectorTarget = undefined;
    var selectorReference = undefined;

    // Check if 'target' exists
    if (
        (Boolean(target)) &&
        (document.querySelector(target))
    ) {
        selectorTarget = document.querySelector(target);
    }
    else {
        console.warn('\'' + target + '\' selector not found');

        return;
    }

    // Check if 'reference' exists
    if (
        (Boolean(reference)) ||
        (document.querySelector(reference))
    ) {
        selectorReference = document.querySelector(reference);
    }

    if (selectorReference) {
        // Get bounding clients
        selectorTarget = selectorTarget.getBoundingClientRect();
        selectorReference = selectorReference.getBoundingClientRect();

        // Position: Y
        const posY = (target === reference) ?
            selectorReference.top :
            (window.scrollY + selectorTarget.top - selectorReference.height);

        // Scroll to the computed position
        window.scrollTo({ top: posY, behavior: behavior });
    }
    else {
        selectorTarget.scrollIntoView({ behavior: behavior });
    }
}

function toolkit_api_location_serializeSearch(dict) {
    const arr = [];
    var result = '';

    for (const key in dict) {
        arr.push(encodeURIComponent(key) + '=' + encodeURIComponent(dict[key]));
    }

    if (arr.length) {
        result = '?' + arr.join('&');
    }

    return result;
}

function toolkit_api_location_serializeHash(list) {
    const arr = [];
    var result = '';

    for (const idx in list) {
        arr.push(encodeURIComponent(list[idx]));
    }

    if (arr.length) {
        result = '#' + arr.join('#');
    }

    return result;
}

function toolkit_api_location_buildUrl(
    path,
    search = {},
    hash = []
) {
    // Initialize the base path
    var result = path;

    // Concatenate the search string
    const search_string = toolkit_api_location_serializeSearch(search);

    if ('' !== search_string) {
        result += search_string;
    }

    // Concatenate the hash string
    const hash_string = toolkit_api_location_serializeHash(hash);

    if ('' !== hash_string) {
        result += hash_string;
    }

    return result;
}

function toolkit_api_location_canBeDisplayed(page, auth_state) {
    return (false === page.is_private) ? true : (auth_state === toolkit_enums_auth_stateTypes.AUTHENTICATED);
}

function toolkit_api_routes_layoutBuilder(layoutDict, isDict = true) {
    return (
        <Route
            element={layoutDict.layout}
        >
            {layoutDict.routes.map((routes, i) => {
                if (isDict) {
                    const { exact = true, ...rest } = routes;

                    return (
                        <Route
                            key={i}
                            exact={exact}
                            {...rest}
                        />
                    );
                }
                else {
                    return (
                        <Fragment
                            key={i}
                        >
                            {routes}
                        </Fragment>
                    );
                }
            })}
        </Route>
    );
}

async function toolkit_api_email_sendEmail({ emailSubject = '', bodyContent = '', replyToList = [], destinationList = [], CcList = [], BccList = [] }) {
    // Amazon SES client configuration
    const ses = new SES(
        {
            region: process.env.REACT_APP__AWS_SDK__SES__REGION,
            credentials:
            {
                accessKeyId: process.env.REACT_APP__AWS_SDK__SES__ACCESS_KEY__ID,
                secretAccessKey: process.env.REACT_APP__AWS_SDK__SES__ACCESS_KEY__SECRET,
            },
        }
    );

    // Email parameters
    const params =
    {
        Source: process.env.REACT_APP__AWS_SDK__SES__MAILER__SOURCE,
        ReplyToAddresses:
            [
                // Reply-to address list
                // Note: Registered AWS SES domain addresses only !!!
                ...replyToList,
            ],
        Destination:
        {
            ToAddresses:
                [
                    // Destination address list
                    // Note: Registered AWS SES domain addresses only !!!
                    ...destinationList,
                ],
            CcAddresses:
                [
                    // CC address list
                    // Note: Registered AWS SES domain addresses only !!!
                    ...CcList,
                ],
            BccAddresses:
                [
                    // BCC address list
                    // Note: Registered AWS SES domain addresses only !!!
                    ...BccList,
                ],
        },
        Message:
        {
            Subject:
            {
                Charset: 'UTF-8',
                Data: emailSubject,
            },
            Body:
            {
                Html:
                {
                    Charset: 'UTF-8',
                    Data: bodyContent,
                },
            },
        },
    };

    // Response handler
    var response = undefined;

    // Create the promise and SES service object
    const sendPromise = ses.sendEmail(params);

    // Process the promise result
    await sendPromise
        .then(
            async (result) => {
                response =
                {
                    status: toolkit_enums_email_statusTypes.SUCCESS,
                    data: result,
                };
            }
        )
        .catch(
            async (err) => {
                response =
                {
                    status: toolkit_enums_email_statusTypes.FAIL,
                    data: err,
                };
            }
        );

    return response;
}

function toolkit_api_utils_strToPix(str_rem) {
    const rem = parseFloat(str_rem);
    const px = rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

    return px;
}

function toolkit_api_utils_decodeStyleStr(name) {
    return getComputedStyle(document.body).getPropertyValue(name);
}

function toolkit_api_utils_getBounding(reference = undefined) {
    var result = undefined;

    // Check if 'reference' exists
    if (
        (Boolean(reference)) &&
        (document.querySelector(reference))
    ) {
        // Get bounding clients
        result = document.querySelector(reference).getBoundingClientRect();
    }

    return result;
}

function toolkit_api_utils_adaptiveOption(viewPort, optionLarge, optionMedium, optionSmall) {
    switch (viewPort) {
        case toolkit_enums_window_viewPortTypes.LARGE:
            return optionLarge;

        case toolkit_enums_window_viewPortTypes.MEDIUM:
            return optionMedium;

        default:
            return optionSmall;
    }
}

async function toolkit_api_utils_msDelay(ms) {
    //
    // usage: await toolkit_api_utils_msDelay(ms);
    //
    return new Promise(resolve => setTimeout(resolve, ms));
};



// --------------------------------------------------------------------------------
//      TOOL KIT ARTIFACTS
// --------------------------------------------------------------------------------

var toolKit =
{
    api: {
        window:
        {
            getScreenProperties: toolkit_api_window_getScreenProperties,
            scrollTo: toolkit_api_window_scrollTo,
        },
        location:
        {
            serializeSearch: toolkit_api_location_serializeSearch,
            serializeHash: toolkit_api_location_serializeHash,
            buildUrl: toolkit_api_location_buildUrl,
            canBeDisplayed: toolkit_api_location_canBeDisplayed,
        },
        routes:
        {
            layoutBuilder: toolkit_api_routes_layoutBuilder,
        },
        email:
        {
            sendEmail: toolkit_api_email_sendEmail,
        },
        utils:
        {
            strToPix: toolkit_api_utils_strToPix,
            decodeStyleStr: toolkit_api_utils_decodeStyleStr,
            getBounding: toolkit_api_utils_getBounding,
            adaptiveOption: toolkit_api_utils_adaptiveOption,
            msDelay: toolkit_api_utils_msDelay,
        },
    },
    enums:
    {
        runtime:
        {
            configTypes: toolkit_enums_runtime_configTypes,
        },
        auth:
        {
            stateTypes: toolkit_enums_auth_stateTypes,
            procStatusTypes: toolkit_enums_auth_procStatusTypes,
        },
        window:
        {
            viewPortTypes: toolkit_enums_window_viewPortTypes,
        },
        device:
        {
            deviceTypes: toolkit_enums_device_deviceTypes,
            osTypes: OsTypes,
            browserTypes: BrowserTypes,
        },
        email:
        {
            statusTypes: toolkit_enums_email_statusTypes,
        }
    },
};



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export {
    toolKit,
};



// End of file
